<script lang="ts" setup>
import type { ContentLink } from '~/models/Content/ContentLink'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faArrowRightLong } from '@fortawesome/duotone-regular-svg-icons'
import { gradeTypeNames } from '~/utils/gradeSorter'
import useProductStore from '~/stores/product'
import { PendoTrackName } from '~/models/Pendo'
import { GradeType } from '~/models/GradeType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useSubjectButton from '~/composables/useSubjectButton'
import useProductHelper from '~/composables/useProductHelper'
import usePendo from '~/composables/usePendo'
import useContentApi from '~/api/contentApi'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'

const { link } = defineProps<{ link: ContentLink }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { buildResourceUrl } = useUrlBuilder()
const { getStyles } = useSubjectButton()
const { isAddonProduct } = useProductHelper()
const { getProductFromResource } = useProductStore()
const { pendoTrack, pendoFlushNow } = usePendo()

const { data: relation } = useQuery({
  queryKey: ['home8to10-funfact', 'dont-invalidate-on-grade-change'],
  queryFn: async () => (await findContents({
    contentIdCriterion: [Number(link?.linkObject?.destinationContentId)],
    mainLocationCriterion: true,
  }))[0],
  staleTime: Infinity,
  enabled: computed(() => link
    && !link.url
    && Number(link.linkObject?.destinationContentId) > 0
  ),
})

const url = computed(() => {
  if (link?.url) {
    return link.url
  }
  if (relation.value) {
    return buildResourceUrl(relation.value)
  }
  return '/'
})

const product = computed(() => relation.value && getProductFromResource(relation.value))
const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  if (isAddonProduct(product.value))
    return product.value.aunivers?.name
  return product.value.subjects?.length ? product.value.subjects[0] : ''
})

const trackFunFact = () => {
  pendoTrack(PendoTrackName.HomeFunFact, {
    title: link.title,
    gradeType: gradeTypeNames[GradeType.Grade8To10],
    url: url.value,
    subject: product.value?.subjects[0] ?? '',
  })
  pendoFlushNow()
}
</script>

<template>
  <div :style="getStyles(subjectOrProductName ?? 'white')" class="flex flex-col justify-between gap-6 rounded-xl p-6 text-(--color) lg:py-12">
    <div class="mb-2">
      <p class="mb-1 text-sm" v-text="t('home.didYouKnow')" />
      <p class="font-tiempos text-3xl" v-text="link?.title" />
    </div>
    <LinkOrAnchor
      v-if="url"
      :link="url"
      class="flex gap-2 font-medium text-balance underline-offset-2 transition-all duration-300 hover:ml-1 hover:underline focus-visible:underline focus-visible:ring"
      @click="trackFunFact"
    >
      <font-awesome-icon
        :icon="faArrowRightLong"
        class="mt-0.5"
        size="lg"
      />
      <span v-dompurify-html="link?.intro" />
    </LinkOrAnchor>
  </div>
</template>
